/**
 * Garantia / RMA — **fila de casos** (Task 15, brief 3.6).
 *
 * Leia isto antes de mexer:
 *
 * 1. **A fila não tem máquina própria.** Etapas, próximos passos e o que cada
 *    passo exige vêm da regra pura (`src/rma/state-machine.ts`); a tela só
 *    lista e navega.
 * 2. **Cada caso navega.** A linha leva ao detalhe do caso e, quando há pedido
 *    vinculado, ao pedido unificado — mesmos registros, mesmo histórico (D-03).
 *    É a fonte real que o drill-down do KPI "garantias abertas" (Task 10) usa.
 * 3. **Fonte ausente não é "nenhum caso".** Sem `/api/rma/cases` a tela declara
 *    a ausência, em vez de uma fila vazia que leria como "nada em garantia".
 *
 * Migrado para `.tsx`/JSX real (Cluster D — Logística & Garantias, redesign
 * por perfil; ver `docs/architecture/ADR-0011-*.md`). Mudança de sintaxe
 * (`createElement` → JSX) + um ícone lucide (`ShieldCheck`, reforço da
 * garantia) na composição por etapa — esta tela não tinha NENHUM ícone antes
 * da migração (lacuna real, achado da investigação inicial do cluster),
 * mesmo padrão aditivo do `ShoppingBag` em `Pedidos.tsx` (Cluster A). Nenhum
 * `data-*`/aria/texto/rota muda.
 */
// Import explícito de `React` — ver o mesmo comentário em `FulfillmentStateMachine.tsx`.
import React, { type ReactElement } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { StageStrip } from "../components/StageStrip.js";
import { EmptyState, PageHeader } from "../ui/index.js";
import { RecordTable } from "../components/RecordTable.js";
import { RmaBoundary } from "../components/RmaBoundary.js";
import { StatusPanel } from "../components/StatusPanel.js";
import { Badge } from "../ui/index.js";
import {
  casesByState,
  isRmaSourceAwaitingContract,
  openCases,
  stateBreakdown
} from "../rma/dataset.js";
import { RMA_STATE_LABELS, isRmaState, type RmaState } from "../rma/state-machine.js";
import { AWAITING_CONTRACT_LABEL } from "../dashboard/kpis.js";
import { formatDateTime } from "../format.js";
import type { RmaDataset } from "../rma/dataset.js";
import type { RmaViewProps } from "../rma/view-props.js";
import type { PlatformRmaCase } from "../api/types.js";

export function RmaFilaTable({ cases }: { cases: PlatformRmaCase[] }): ReactElement {
  return (
    <RecordTable<PlatformRmaCase>
      caption="Casos de garantia/assistência, com etapa, responsável e prazo"
      rows={cases}
      idOf={(rmaCase) => rmaCase.id}
      emptyMessage="Nenhum caso neste recorte."
      // Fase 7 (Plano de Ataque R8): rollout mecânico do primitivo da Fase 6.
      animateRows
      columns={[
        {
          key: "id",
          label: "Caso",
          render: (rmaCase) => (
            <span className="inline-flex flex-wrap items-center gap-2">
              <Link to={`/garantias/${rmaCase.id}`}>{rmaCase.protocolCode ?? rmaCase.id}</Link>
              {rmaCase.demo ? <Badge tone="warning" data-demo-badge="true">Exemplo</Badge> : null}
            </span>
          )
        },
        {
          key: "state",
          label: "Etapa",
          render: (rmaCase) => <span data-rma-state={rmaCase.state}>{RMA_STATE_LABELS[rmaCase.state]}</span>
        },
        {
          key: "order",
          label: "Pedido",
          render: (rmaCase) =>
            rmaCase.platformOrderId ? (
              <span data-order-external-id={rmaCase.orderExternalId ?? ""}>
                <Link to={`/pedidos/${rmaCase.platformOrderId}`} title="Abrir o pedido que originou o caso">
                  {rmaCase.orderExternalId ?? rmaCase.platformOrderId}
                </Link>
              </span>
            ) : (
              <span>—</span>
            )
        },
        { key: "customer", label: "Cliente", render: (rmaCase) => rmaCase.customerName ?? rmaCase.customerId },
        { key: "product", label: "Produto", render: (rmaCase) => rmaCase.productName ?? rmaCase.productId ?? "—" },
        {
          key: "due",
          label: "Prazo",
          render: (rmaCase) => (rmaCase.dueAt ? formatDateTime(rmaCase.dueAt) : "—")
        },
        { key: "openedAt", label: "Aberto em", render: (rmaCase) => formatDateTime(rmaCase.openedAt) }
      ]}
    />
  );
}

/** Composição por etapa — o que a fila contém, declarado, com atalho de recorte por etapa. */
function StateSummary({ cases, state }: { cases: RmaDataset; state: RmaState | null }): ReactElement {
  const rows = stateBreakdown(cases);
  return (
    <StageStrip
      ariaLabel="Composição dos casos por etapa"
      total={cases.cases.length}
      data-state-summary={cases.cases.length}
      activeKey={state}
      items={rows.map((row) => ({ key: row.state, label: row.label, count: row.count, to: `/garantias?state=${row.state}` }))}
    />
  );
}

export function RmaView({ cases, state = null, onlyOpen = false }: RmaViewProps): ReactElement {
  if (isRmaSourceAwaitingContract(cases)) {
    return (
      <div className="page page--garantias flex flex-col gap-6" data-page="garantias">
        <PageHeader title="Garantias / RMA" />
        <StatusPanel
          kind="awaiting_source"
          title={`Fila de garantias/RMA ${AWAITING_CONTRACT_LABEL}`}
          detail="A rota /api/rma/cases não respondeu neste ambiente. Os casos aparecem quando a rota for publicada — exibir uma fila vazia diria, falsamente, que não há garantia aberta."
        />
      </div>
    );
  }

  const openTotal = openCases(cases).length;
  const filtered = onlyOpen ? openCases(cases) : state ? casesByState(cases, state) : cases.cases;

  return (
    <div className="page page--garantias flex flex-col gap-6" data-page="garantias">
      <PageHeader
        title="Garantias / RMA"
        description="Casos de garantia e assistência, da abertura do defeito até o encerramento."
        actions={
          onlyOpen || state ? (
            <Link to="/garantias" className="text-sm font-bold text-brand-text underline">
              Ver todos os casos
            </Link>
          ) : null
        }
      />
      <p className="m-0 text-sm font-bold text-muted" data-rma-count={filtered.length}>
        {onlyOpen
          ? `${filtered.length} caso(s) em aberto.`
          : state
            ? `${filtered.length} caso(s) na etapa "${RMA_STATE_LABELS[state]}".`
            : `${filtered.length} caso(s) na fila.`}
      </p>
      {/* Resumos/avisos de escopo da fila, preservando data-state-summary/data-rma-open
          e o link "ver só os abertos". */}
      <section className="flex flex-col gap-3 rounded-panel border border-border bg-surface p-5 shadow-s1">
        <StateSummary cases={cases} state={state} />
        <p className="page__note m-0 flex items-center gap-2 text-sm text-muted" data-rma-open={openTotal}>
          <ShieldCheck className="h-4 w-4 shrink-0 text-brand-text" aria-hidden focusable={false} />
          {openTotal === 0 ? "Nenhum caso em aberto." : `Casos em aberto (${openTotal}). `}
          {openTotal > 0 ? (
            <Link to="/garantias?open=1" className="page__note-link font-bold text-brand-text hover:underline">
              ver só os abertos
            </Link>
          ) : null}
        </p>
        <p className="page__note m-0 text-xs text-muted">
          O caso não recadastra o pedido nem o cliente: cada caso referencia o pedido unificado e o cliente canônico
          (mesmos registros, mesmo histórico). Reembolso/troca gera referência para o Financeiro — não executa o pagamento.
        </p>
      </section>
      {filtered.length === 0 ? (
        <EmptyState icon={ShieldCheck} title="Nenhum caso neste recorte." description="Quando um cliente relatar defeito, o caso aparece aqui com etapa, responsável e prazo." />
      ) : (
        <RmaFilaTable cases={filtered} />
      )}
    </div>
  );
}

export function Rma(): ReactElement {
  const [searchParams] = useSearchParams();
  const stateParam = searchParams.get("state");
  const state: RmaState | null = isRmaState(stateParam) ? stateParam : null;
  // O drill-down do KPI "garantias abertas" (Task 10) navega para cá com
  // `?kpi=garantias_abertas`: o indicador conta os casos NÃO encerrados, então
  // o recorte que ele abre é exatamente a fila de casos em aberto. É a ligação
  // do KPI à FONTE real (rma_cases) exigida pela Task 15.
  const fromOpenKpi = searchParams.get("kpi") === "garantias_abertas";
  const onlyOpen = searchParams.get("open") === "1" || fromOpenKpi;
  return <RmaBoundary>{(cases: RmaDataset) => <RmaView cases={cases} state={state} onlyOpen={onlyOpen} />}</RmaBoundary>;
}
